// /deployment — per deployment mode: who operates it and what leaves your environment.
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { SitePage, Btn, Go, TO, asset } from './SiteChrome';
import { MODES } from './content';

const SPLIT = [
  {
    eyebrow: '01 Restricted work', eyebrowInk: '#F2CE49', bg: '#2C3123', ink: '#F0EEE8', body: '#D5D8CC',
    title: 'Private models only',
    text: 'Work you classify as restricted runs on private models, self-hosted in your environment. There is no external fallback for restricted work.',
  },
  {
    eyebrow: '02 Approved work', eyebrowInk: '#08090A', bg: '#F2CE49', ink: '#08090A', body: '#2C3123',
    title: 'Approved third-party models',
    text: "Work you permit to use approved third-party models sends the permitted inputs to that provider, under that provider's terms.",
  },
  {
    eyebrow: '03 Two distinctions', eyebrowInk: '#B84A1C', bg: '#FFFFFF', ink: '#08090A', body: 'var(--text-secondary)',
    title: 'Permissions, routing, execution',
    text: 'Team permissions are distinct from task routing. Agent execution is separate from where the platform and the underlying model are hosted.',
  },
];

function DeploymentPage() {
  return (
    <SitePage>
      <section className="lsl-wrap lsl-page-hero">
        <div>
          <h1 className="lsl-page-h1">Choose where it runs. Keep your options open.</h1>
          <p className="lsl-page-lede">
            Choose where it runs, who operates it and which models the work may use. This page states, per deployment
            mode, what leaves your environment.
          </p>
        </div>
        <div className="lsl-art-frame" style={{ background: '#E1642F' }}>
          <div aria-hidden style={{ backgroundImage: `url("${asset('/landing/deployment-hero.svg')}")` }} />
        </div>
      </section>

      <section className="lsl-wrap lsl-sec lsl-stack">
        {MODES.map((m) => (
          <div key={m.num} className="lsl-dmode">
            <div className="lsl-dmode-art" style={{ background: m.bg }}>
              <div className="lsl-face-top">
                <span className="lsl-eyebrow">{m.eyebrow}</span>
                <span className="lsl-eyebrow">{m.num}</span>
              </div>
              <img src={asset(m.art)} alt="" aria-hidden />
            </div>
            <div className="lsl-dmode-body">
              <div>
                <h2>{m.name}</h2>
                <p>{m.body}</p>
              </div>
              <div className="lsl-fact">
                <div className="lsl-eyebrow">What leaves your environment</div>
                <div>{m.leaves}</div>
                <div className="lsl-fact-note">{m.note}</div>
              </div>
              <div className="lsl-fact">
                <div className="lsl-eyebrow">Who operates it</div>
                <div>{m.operator}</div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="lsl-band lsl-band--both">
        <div className="lsl-wrap lsl-pad">
          <h2 className="lsl-h2" style={{ maxWidth: '24ch' }}>Restricted work and permitted work are set separately.</h2>
          <div className="lsl-split">
            {SPLIT.map((c) => (
              <div key={c.title} style={{ background: c.bg, boxShadow: c.bg === '#FFFFFF' ? '0 1px 0 rgba(8,9,10,.06)' : undefined }}>
                <span className="lsl-eyebrow" style={{ color: c.eyebrowInk }}>{c.eyebrow}</span>
                <strong style={{ color: c.ink }}>{c.title}</strong>
                <p style={{ color: c.body }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lsl-wrap lsl-sec lsl-sec--tall" style={{ paddingTop: 'var(--space-12)' }}>
        <div className="lsl-closing" style={{ backgroundImage: `url("${asset('/landing/cta-ground.svg')}")` }}>
          <div>
            <h2>We will work through supported components, model requirements and operating arrangements for your deployment.</h2>
            <div className="lsl-closing-links">
              <Go to={TO.security}>Security and trust</Go>
              <Go to={TO.gateway}>Model access in LatentGateway</Go>
            </div>
            <Btn to={TO.contact} size="lg" iconRight={ArrowRight}>Request a scoped review</Btn>
          </div>
        </div>
      </section>
    </SitePage>
  );
}

export default DeploymentPage;
